/**
 * ProtectedRoute Component
 *
 * Route wrapper that requires authentication to access.
 * Redirects unauthenticated users to login page while preserving intended destination.
 * Redirects pending_payment users to /payment page.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// 2. Internal absolute imports
import { useAuth } from '@/hooks/useAuth';
import { usersRepository } from '@/repositories/usersRepository';
import { Card, CardBody, Alert, Button } from '@enfomentis/ui';

export interface ProtectedRouteProps {
  /** Content to render when authenticated */
  children: React.ReactNode;
}

/**
 * Protected Route Component
 *
 * Wraps routes that require authentication. Features:
 * - Checks authentication status using useAuth hook
 * - Shows loading state while auth status is being determined
 * - Redirects unauthenticated users to /login with return URL
 * - Renders children when user is authenticated
 *
 * Usage:
 * ```tsx
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({ children }: ProtectedRouteProps): React.JSX.Element {
  const { authState, logout } = useAuth();
  const location = useLocation();
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Fetch user status from database via repository
  useEffect(() => {
    async function fetchStatus() {
      if (!authState.isAuthenticated || !authState.user?.id) {
        setStatusLoading(false);
        return;
      }

      try {
        const user = await usersRepository.getById(authState.user.id);
        if (user) {
          setUserStatus(user.status);
        }
      } catch (err) {
        console.error('Failed to fetch user status:', err);
      } finally {
        setStatusLoading(false);
      }
    }

    if (!authState.isLoading) {
      fetchStatus();
    }
  }, [authState.isAuthenticated, authState.user?.id, authState.isLoading]);

  // Show loading state while authentication status is being determined
  if (authState.isLoading || statusLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-default-500">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving intended destination
  if (!authState.isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Redirect pending_payment users to payment page (unless already on payment routes)
  const isPaymentRoute = location.pathname.startsWith('/payment');
  if (userStatus === 'pending_payment' && !isPaymentRoute) {
    return <Navigate to="/payment" replace />;
  }

  // Redirect inactive users to subscribe page to re-subscribe
  // Allow /payment/* routes so post-checkout success page works during webhook processing
  if (userStatus === 'inactive' && !isPaymentRoute) {
    return <Navigate to="/subscribe" replace />;
  }

  // Block admin accounts from accessing journal routes
  if (authState.user?.role === 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md">
          <CardBody className="space-y-4">
            <Alert color="warning" variant="flat">
              <div className="space-y-2">
                <p className="font-semibold">Admin Account Detected</p>
                <p className="text-sm">
                  Admin accounts cannot access the trading journal.
                  Please sign in with your trading account to access journal features.
                </p>
              </div>
            </Alert>
            <div className="flex gap-2">
              <Button
                color="primary"
                className="flex-1"
                onPress={() => window.location.href = '/admin'}
              >
                Go to Admin Dashboard
              </Button>
              <Button
                color="default"
                variant="flat"
                className="flex-1"
                onPress={async () => {
                  await logout();
                  window.location.href = '/login';
                }}
              >
                Sign Out
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Render protected content when authenticated
  return <>{children}</>;
}
